import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, ChevronDown, Download, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function FinanceReportView() {
  const { reportType } = useParams();
  const [loadingExport, setLoadingExport] = useState(false);

  // Format title based on ID
  const title = reportType.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const handleExportPDF = () => {
    setLoadingExport(true);
    try {
      const doc = new jsPDF();
      
      // Setup title
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("SATALA DERMATECH ESSENTIAL", doc.internal.pageSize.getWidth() / 2, 20, { align: "center" });
      
      doc.setFontSize(16);
      doc.text(title, doc.internal.pageSize.getWidth() / 2, 28, { align: "center" });
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Per tanggal 26/06/2026", doc.internal.pageSize.getWidth() / 2, 34, { align: "center" });
      doc.text("(dalam IDR)", doc.internal.pageSize.getWidth() / 2, 39, { align: "center" });

      // Dummy Data for Autotable
      const tableData = [
        [{ content: 'Aktiva', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [249, 245, 236] } }],
        [{ content: 'Aktiva Lancar', colSpan: 2, styles: { fontStyle: 'bold' } }],
        ['(1-1001) - Rekening Bank', '239.624.200,00'],
        ['(1-1002) - Total Kas', '22.872.392,00'],
        ['(1-1200) - Piutang Usaha', '292.867.492,00'],
        ['(1-1400) - Persediaan Barang', '195.512.000,00'],
        [{ content: 'Total Aktiva Lancar', styles: { fontStyle: 'bold' } }, { content: '750.876.084,00', styles: { fontStyle: 'bold' } }],
        
        [{ content: 'Aktiva Tetap', colSpan: 2, styles: { fontStyle: 'bold' } }],
        ['(1-1800) - Aset Tetap', '79.115.000,00'],
        [{ content: 'Total Aktiva Tetap', styles: { fontStyle: 'bold' } }, { content: '79.115.000,00', styles: { fontStyle: 'bold' } }],
        
        [{ content: 'Total Aktiva', styles: { fontStyle: 'bold', fillColor: [230, 230, 230] } }, { content: '829.991.084,00', styles: { fontStyle: 'bold', fillColor: [230, 230, 230] } }],
      ];

      doc.autoTable({
        startY: 45,
        body: tableData,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 50, halign: 'right', textColor: [79, 148, 205] } // Blue-ish text for numbers like Mekari
        },
        willDrawCell: function(data) {
          // Draw bottom border for totals
          if (data.cell.raw && data.cell.raw.content && data.cell.raw.content.toString().startsWith('Total')) {
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
          }
        }
      });

      doc.save(`${reportType}_satala.pdf`);
    } catch (error) {
      console.error("Failed to generate PDF", error);
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in zoom-in duration-500">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-black text-[#302621] tracking-tight">{title} <span className="text-sm font-normal text-slate-500">(dalam IDR)</span></h1>
        
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#E6D5B8] text-[#7A695B]">
            Template <ChevronDown className="w-4 h-4 ml-2" />
          </Button>
          <Button 
            onClick={handleExportPDF}
            disabled={loadingExport}
            className="bg-[#8C6208] hover:bg-[#A37B24] text-white shadow-md"
          >
            {loadingExport ? 'Mengekspor...' : 'Ekspor PDF'} <Download className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="p-4 bg-white border border-[#E6D5B8]/60 rounded-t-xl flex flex-col md:flex-row gap-4 items-end">
        <div className="flex-1 w-full flex gap-3">
          <div className="w-full">
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Tanggal awal</label>
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                defaultValue="26/06/2026"
                className="w-full px-3 py-2 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30"
              />
            </div>
          </div>
          <div className="w-full">
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Tanggal akhir</label>
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                defaultValue="26/06/2026"
                className="w-full px-3 py-2 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30"
              />
            </div>
          </div>
          <div className="w-full">
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Periode</label>
            <select className="w-full px-3 py-2 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30">
              <option>Hari ini</option>
              <option>Bulan ini</option>
              <option>Tahun ini</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-xl">
            Filter
          </Button>
          <Button variant="outline" className="border-[#E6D5B8] text-[#7A695B] rounded-xl">
            <Filter className="w-4 h-4 mr-2" /> Filter lainnya
          </Button>
        </div>
      </div>

      {/* Empty State / Content area */}
      <div className="bg-[#FFFDF9] border border-[#E6D5B8]/60 border-t-0 rounded-b-xl shadow-sm min-h-[50vh] flex flex-col items-center justify-center p-12 text-center">
        <div className="relative mb-6">
          <div className="w-32 h-32 bg-indigo-50 rounded-full flex items-center justify-center">
             <div className="flex items-end gap-2 h-16 w-16">
               <div className="w-4 h-8 bg-pink-400 rounded-sm"></div>
               <div className="w-4 h-16 bg-indigo-500 rounded-sm"></div>
               <div className="w-4 h-12 bg-pink-300 rounded-sm"></div>
             </div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-20 h-20 text-pink-500/80 -mt-4 transform rotate-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12A10 10 0 1 1 12 2a10 10 0 0 1 10 10z"></path><path d="M22 12A10 10 0 0 0 12 2"></path></svg>
          </div>
        </div>
        <h3 className="text-lg font-bold text-[#302621] mb-1">Laporan akan muncul di sini</h3>
        <p className="text-sm text-[#7A695B]">Pilih tanggal atau periode, lalu klik tombol <span className="font-semibold text-slate-600">Filter</span>.</p>
        
        <p className="mt-8 text-xs text-orange-500 bg-orange-50 px-4 py-2 rounded-full border border-orange-200">
          *Klik tombol <strong>Ekspor PDF</strong> di pojok kanan atas untuk melihat format contoh laporannya.
        </p>
      </div>
    </div>
  );
}
